import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Users, AlertCircle, FileText } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const CART_STORAGE_KEY = 'toindoali_cart_v1';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCartFirstTraveler = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return null;
    const items = JSON.parse(raw);
    if (!Array.isArray(items) || items.length === 0) return null;
    const item = items[0];
    if (item.allTravelers && item.allTravelers.length > 0) return item.allTravelers[0];
    if (item.personalData) return item.personalData;
    return null;
  } catch { return null; }
};

const CheckoutStep2 = () => {
  const navigate = useNavigate();
  const { items: cartItems } = useCart();
  const [bookingData, setBookingData] = useState(null);
  const [travelers, setTravelers] = useState([]);
  const [errors, setErrors] = useState({});
  const [minorCompanions, setMinorCompanions] = useState({}); // { index: companion_type }
  const [minorEvaluations, setMinorEvaluations] = useState({}); // { index: evaluation }

  const emptyTraveler = {
    cpf: '',
    fullName: '',
    gender: '',
    documentType: 'cpf',
    documentNumber: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    phone: '',
    email: ''
  };

  useEffect(() => {
    const data = sessionStorage.getItem('bookingData');
    if (!data) {
      navigate('/');
      return;
    }
    const parsed = JSON.parse(data);
    setBookingData(parsed);
    
    // Verificar se cliente está logado
    const customerAuth = localStorage.getItem('customerData');
    let customerData = null;
    if (customerAuth) {
      try {
        customerData = JSON.parse(customerAuth);
      } catch (e) {
        console.error('Erro ao parsear dados do cliente:', e);
      }
    }

    // Se há itens no carrinho, usa o viajante principal do carrinho como fallback
    // (substitui o customer apenas se não houver customer logado).
    const cartFirstTraveler = (cartItems && cartItems.length > 0) ? getCartFirstTraveler() : null;
    if (!customerData && cartFirstTraveler) {
      customerData = {
        full_name: cartFirstTraveler.fullName || cartFirstTraveler.name || '',
        cpf: (cartFirstTraveler.cpf || '').replace(/\D/g, ''),
        gender: cartFirstTraveler.gender || '',
        phone: cartFirstTraveler.phone || '',
        email: cartFirstTraveler.email || '',
        birth_date: (cartFirstTraveler.birthDay && cartFirstTraveler.birthMonth && cartFirstTraveler.birthYear)
          ? `${cartFirstTraveler.birthDay}/${cartFirstTraveler.birthMonth}/${cartFirstTraveler.birthYear}`
          : '',
        rg: cartFirstTraveler.documentType === 'rg' ? cartFirstTraveler.documentNumber : '',
        passport: cartFirstTraveler.documentType === 'passaporte' ? cartFirstTraveler.documentNumber : ''
      };
    }
    
    // Verificar se é viagem aérea
    const isAereo = parsed.packageCategory?.includes('aereo');
    
    // Inicializar array de viajantes baseado na quantidade
    const qty = parsed.quantity || 1;
    const initialTravelers = Array(qty).fill(null).map((_, index) => {
      // Se é o primeiro viajante e o cliente está logado, preencher automaticamente
      if (index === 0 && customerData) {
        // Parsear data de nascimento (formato: DD/MM/YYYY)
        let birthDay = '', birthMonth = '', birthYear = '';
        if (customerData.birth_date) {
          const parts = customerData.birth_date.split('/');
          if (parts.length === 3) {
            // Converter para número e depois string para remover zeros à esquerda
            // O Select usa valores "1", "2", "3"... não "01", "02", "03"...
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            if (!isNaN(day) && day >= 1 && day <= 31) {
              birthDay = day.toString();
            }
            if (!isNaN(month) && month >= 1 && month <= 12) {
              birthMonth = month.toString();
            }
            if (parts[2] && parts[2].length === 4) {
              birthYear = parts[2];
            }
          }
        }
        
        // Se é viagem aérea e cliente tem RG, usar RG como documento
        const hasRG = customerData.rg && customerData.rg.trim() !== '';
        const hasPassport = customerData.passport && customerData.passport.trim() !== '';
        
        // Verificar se requer passaporte (internacional)
        const requiresPassport = parsed.requiresPassport === true;
        
        // Determinar tipo de documento e número
        let docType = 'cpf';
        let docNumber = '';
        
        if (requiresPassport && hasPassport) {
          // Viagem internacional com passaporte cadastrado
          docType = 'passaporte';
          docNumber = customerData.passport;
        } else if (isAereo && hasRG) {
          // Viagem aérea nacional com RG cadastrado
          docType = 'rg';
          docNumber = customerData.rg;
        } else if (hasPassport) {
          // Tem passaporte cadastrado (pode usar em qualquer viagem)
          docType = 'passaporte';
          docNumber = customerData.passport;
        }
        
        return {
          cpf: customerData.cpf ? (customerData.cpf.length === 11 
            ? customerData.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') 
            : customerData.cpf) : '',
          fullName: customerData.full_name || '',
          gender: customerData.gender || '',
          documentType: docType,
          documentNumber: docNumber,
          birthDay,
          birthMonth,
          birthYear,
          phone: customerData.phone || '',
          email: customerData.email || ''
        };
      }
      return { ...emptyTraveler };
    });
    setTravelers(initialTravelers);
  }, [navigate, cartItems]);

  // Formatar CPF: xxx.xxx.xxx-xx
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
  };

  // Formatar WhatsApp: (xx) xxxxx-xxxx
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  // Validar email
  const isValidEmail = (email) => {
    return email.includes('@') && email.includes('.');
  };

  // Verificar se precisa de documento adicional (apenas aéreos)
  const needsAdditionalDocument = () => {
    const category = bookingData?.packageCategory;
    // Passeio de 1 dia e rodoviário não precisam
    if (category === 'passeio-1-dia' || category === 'rodoviario') {
      return false;
    }
    // Aéreos precisam de RG ou Passaporte
    return true;
  };

  // Verificar se é viagem que requer PASSAPORTE (baseado na flag do pacote)
  const requiresPassport = () => {
    // Usa a flag requires_passport do pacote (definida no admin)
    return bookingData?.requiresPassport === true;
  };

  // Cálculo de idade na data da viagem
  const ageAtTrip = (t) => {
    if (!t?.birthDay || !t?.birthMonth || !t?.birthYear) return null;
    const tripStr = bookingData?.selectedDate || '';
    // Extrai primeiro DD/MM/YYYY
    const m = tripStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!m) return null;
    const tripD = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    const birthD = new Date(parseInt(t.birthYear, 10), parseInt(t.birthMonth, 10) - 1, parseInt(t.birthDay, 10));
    let age = tripD.getFullYear() - birthD.getFullYear();
    if (tripD.getMonth() < birthD.getMonth() || (tripD.getMonth() === birthD.getMonth() && tripD.getDate() < birthD.getDate())) age -= 1;
    return age;
  };

  // Traveler é menor de idade na viagem?
  const isMinorAtTrip = (t) => {
    const a = ageAtTrip(t);
    return a !== null && a < 18;
  };

  // Chama backend para avaliar documentação necessária
  const evaluateMinor = async (index, companionType) => {
    const t = travelers[index];
    if (!t || !t.birthDay) return;
    const birth = `${t.birthDay.padStart(2, '0')}/${t.birthMonth.padStart(2, '0')}/${t.birthYear}`;
    const tripMatch = (bookingData?.selectedDate || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!tripMatch) return;
    const trip = `${tripMatch[1].padStart(2, '0')}/${tripMatch[2].padStart(2, '0')}/${tripMatch[3]}`;
    try {
      const r = await axios.post(`${API_URL}/api/minors/evaluate`, {
        birth_date: birth,
        trip_start_date: trip,
        is_international: requiresPassport(),
        companion_type: companionType,
      });
      setMinorEvaluations(prev => ({ ...prev, [index]: r.data }));
    } catch (e) { console.error('Erro ao avaliar menor', e); }
  };

  const setCompanionType = (index, val) => {
    setMinorCompanions(prev => ({ ...prev, [index]: val }));
    if (val) evaluateMinor(index, val);
  };

  const minorIndices = useMemo(() => travelers.map((t, i) => isMinorAtTrip(t) ? i : -1).filter(i => i >= 0), [travelers, bookingData]);

  const handleTravelerChange = (index, field, value) => {
    let formattedValue = value;
    
    // Aplicar formatação
    if (field === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (field === 'phone') {
      formattedValue = formatPhone(value);
    }
    
    setTravelers(prev => {
      const newTravelers = [...prev];
      newTravelers[index] = { ...newTravelers[index], [field]: formattedValue };
      return newTravelers;
    });
    
    // Limpar erro do campo
    setErrors(prev => ({
      ...prev,
      [`${index}_${field}`]: null
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    for (let i = 0; i < travelers.length; i++) {
      const t = travelers[i];
      
      // CPF obrigatório e com 11 dígitos
      const cpfNumbers = t.cpf.replace(/\D/g, '');
      if (!cpfNumbers || cpfNumbers.length !== 11) {
        newErrors[`${i}_cpf`] = 'CPF deve ter 11 dígitos';
      }
      
      // Nome obrigatório
      if (!t.fullName || t.fullName.trim().length < 3) {
        newErrors[`${i}_fullName`] = 'Nome completo é obrigatório';
      }
      
      // Sexo obrigatório
      if (!t.gender) {
        newErrors[`${i}_gender`] = 'Selecione o sexo';
      }
      
      // Data de nascimento
      if (!t.birthDay || !t.birthMonth || !t.birthYear) {
        newErrors[`${i}_birthDate`] = 'Data de nascimento é obrigatória';
      }
      
      // WhatsApp obrigatório e com 11 dígitos
      const phoneNumbers = t.phone.replace(/\D/g, '');
      if (!phoneNumbers || phoneNumbers.length !== 11) {
        newErrors[`${i}_phone`] = 'WhatsApp deve ter 11 dígitos (DDD + número)';
      }
      
      // Email obrigatório e válido
      if (!t.email) {
        newErrors[`${i}_email`] = 'E-mail é obrigatório';
      } else if (!isValidEmail(t.email)) {
        newErrors[`${i}_email`] = 'E-mail parece estar incorreto (deve conter @)';
      }
      
      // Documento adicional apenas para aéreos
      if (needsAdditionalDocument()) {
        // Viagem internacional fora da América do Sul = PASSAPORTE OBRIGATÓRIO
        if (requiresPassport()) {
          if (t.documentType !== 'passaporte' || !t.documentNumber) {
            newErrors[`${i}_document`] = 'PASSAPORTE é OBRIGATÓRIO para viagens internacionais fora da América do Sul';
          }
        } else {
          // Nacional aéreo = RG ou Passaporte
          if (!t.documentType || !t.documentNumber) {
            newErrors[`${i}_document`] = 'RG ou Passaporte é obrigatório para viagens aéreas';
          }
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (!validateForm()) {
      // Scroll para o primeiro erro
      const firstErrorElement = document.querySelector('.border-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Salvar dados - o primeiro viajante é o responsável
    const updatedData = { 
      ...bookingData, 
      personalData: travelers[0],
      allTravelers: travelers,
      minorCompanions,
      minorEvaluations,
    };
    sessionStorage.setItem('bookingData', JSON.stringify(updatedData));
    navigate('/checkout/embarque');
  };

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const renderTravelerForm = (traveler, index) => (
    <Card key={index} className={index > 0 ? 'mt-6' : ''}>
      <CardContent className="p-6">
        <div className="flex items-center space-x-3 mb-6 pb-4 border-b">
          {index === 0 ? (
            <User className="w-6 h-6 text-[#FFD700]" />
          ) : (
            <Users className="w-6 h-6 text-[#003366]" />
          )}
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {index === 0 ? 'Responsável pela Compra' : `Viajante ${index + 1}`}
            </h3>
            {index === 0 && (
              <p className="text-sm text-gray-600">Dados do titular da reserva</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* CPF */}
          <div>
            <Label>CPF *</Label>
            <Input
              placeholder="000.000.000-00"
              value={traveler.cpf}
              onChange={(e) => handleTravelerChange(index, 'cpf', e.target.value)}
              maxLength={14}
              className={`mt-1 ${errors[`${index}_cpf`] ? 'border-red-500' : ''}`}
            />
            {errors[`${index}_cpf`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_cpf`]}
              </p>
            )}
          </div>

          {/* Nome Completo */}
          <div>
            <Label>Nome Completo *</Label>
            <Input
              placeholder="Nome completo"
              value={traveler.fullName}
              onChange={(e) => handleTravelerChange(index, 'fullName', e.target.value)}
              className={`mt-1 ${errors[`${index}_fullName`] ? 'border-red-500' : ''}`}
            />
            {errors[`${index}_fullName`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_fullName`]}
              </p>
            )}
          </div>

          {/* Sexo */}
          <div>
            <Label>Sexo *</Label>
            <Select value={traveler.gender} onValueChange={(v) => handleTravelerChange(index, 'gender', v)}>
              <SelectTrigger className={`mt-1 ${errors[`${index}_gender`] ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="masculino">Masculino</SelectItem>
                <SelectItem value="feminino">Feminino</SelectItem>
              </SelectContent>
            </Select>
            {errors[`${index}_gender`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_gender`]}
              </p>
            )}
          </div>

          {/* Documento Adicional - apenas para aéreos */}
          {needsAdditionalDocument() && (
            <>
              <div>
                <Label>
                  Documento para Embarque * 
                  {requiresPassport() ? ' (Internacional - Passaporte Obrigatório)' : ' (Aéreo Nacional)'}
                </Label>
                {requiresPassport() ? (
                  // Viagem internacional = Apenas Passaporte
                  <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm font-semibold">
                      ⚠️ PASSAPORTE OBRIGATÓRIO para viagens internacionais fora da América do Sul
                    </p>
                    <Input
                      type="hidden"
                      value="passaporte"
                      onChange={() => handleTravelerChange(index, 'documentType', 'passaporte')}
                    />
                  </div>
                ) : (
                  // Viagem nacional = RG ou Passaporte
                  <Select value={traveler.documentType} onValueChange={(v) => handleTravelerChange(index, 'documentType', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rg">RG</SelectItem>
                      <SelectItem value="passaporte">Passaporte</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label>
                  {requiresPassport() ? 'Número do Passaporte *' : `Número do ${traveler.documentType === 'passaporte' ? 'Passaporte' : 'RG'} *`}
                </Label>
                <Input
                  placeholder={requiresPassport() ? 'Número do Passaporte' : `Número do ${traveler.documentType === 'passaporte' ? 'Passaporte' : 'RG'}`}
                  value={traveler.documentNumber}
                  onChange={(e) => {
                    // Se for internacional, forçar tipo passaporte
                    if (requiresPassport() && traveler.documentType !== 'passaporte') {
                      handleTravelerChange(index, 'documentType', 'passaporte');
                    }
                    handleTravelerChange(index, 'documentNumber', e.target.value);
                  }}
                  className={`mt-1 ${errors[`${index}_document`] ? 'border-red-500' : ''}`}
                />
                {errors[`${index}_document`] && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors[`${index}_document`]}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Data de Nascimento */}
          <div className="md:col-span-2">
            <Label>Data de Nascimento *</Label>
            <div className={`grid grid-cols-3 gap-2 mt-1 ${errors[`${index}_birthDate`] ? 'border border-red-500 rounded-lg p-1' : ''}`}>
              <Select value={traveler.birthDay} onValueChange={(v) => handleTravelerChange(index, 'birthDay', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Dia" />
                </SelectTrigger>
                <SelectContent>
                  {days.map(d => <SelectItem key={d} value={d.toString()}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={traveler.birthMonth} onValueChange={(v) => handleTravelerChange(index, 'birthMonth', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={traveler.birthYear} onValueChange={(v) => handleTravelerChange(index, 'birthYear', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {errors[`${index}_birthDate`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_birthDate`]}
              </p>
            )}
          </div>

          {/* Celular */}
          <div>
            <Label>Celular (WhatsApp) *</Label>
            <Input
              placeholder="(00) 00000-0000"
              value={traveler.phone}
              onChange={(e) => handleTravelerChange(index, 'phone', e.target.value)}
              maxLength={15}
              className={`mt-1 ${errors[`${index}_phone`] ? 'border-red-500' : ''}`}
            />
            {errors[`${index}_phone`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_phone`]}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label>E-mail *</Label>
            <Input
              type="email"
              placeholder="email@exemplo.com"
              value={traveler.email}
              onChange={(e) => handleTravelerChange(index, 'email', e.target.value)}
              className={`mt-1 ${errors[`${index}_email`] ? 'border-red-500' : ''}`}
            />
            {errors[`${index}_email`] && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors[`${index}_email`]}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!bookingData) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 min-w-max px-4">
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center font-bold text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">Seleção</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-[#FFD700]"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#FFD700] flex items-center justify-center font-bold text-gray-900 text-xs md:text-sm">2</div>
              <span className="ml-1 md:ml-2 font-medium text-gray-900 text-xs md:text-sm">Dados</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">3</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">Embarque</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">4</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">Contrato</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600 text-xs md:text-sm">5</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-600">Pagamento</span>
            </div>
          </div>
        </div>

        {/* Info sobre quantidade de viajantes */}
        {bookingData.quantity > 1 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-blue-600" />
              <p className="text-blue-800">
                <strong>{bookingData.quantity} viajantes</strong> - Por favor, preencha os dados de todos os participantes da viagem.
              </p>
            </div>
          </div>
        )}

        {/* Info sobre documentos por categoria */}
        {!needsAdditionalDocument() && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              <strong>Ótima notícia!</strong> Para {bookingData.packageCategory === 'passeio-1-dia' ? 'passeios de 1 dia' : 'viagens rodoviárias'}, você precisa apenas do CPF. Não é necessário RG ou Passaporte.
            </p>
          </div>
        )}

        {needsAdditionalDocument() && (
          <div className={`mb-6 p-4 rounded-lg ${requiresPassport() ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
            {requiresPassport() ? (
              <p className="text-red-800 text-sm">
                <strong>⚠️ VIAGEM INTERNACIONAL:</strong> É OBRIGATÓRIO informar o <strong>PASSAPORTE</strong> para viagens fora da América do Sul.
              </p>
            ) : (
              <p className="text-amber-800 text-sm">
                <strong>Viagem aérea nacional:</strong> É obrigatório informar RG ou Passaporte para embarque.
              </p>
            )}
          </div>
        )}

        {/* Formulários dos Viajantes */}
        {travelers.map((traveler, index) => renderTravelerForm(traveler, index))}

        {/* Painel de MENORES DE IDADE */}
        {minorIndices.length > 0 && (
          <Card className="mt-6 border-purple-300 bg-purple-50" data-testid="minors-panel">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-purple-200">
                <FileText className="w-5 h-5 text-purple-700" />
                <h3 className="text-lg font-bold text-purple-900">Menores de Idade Identificados</h3>
              </div>
              <p className="text-sm text-purple-800 mb-4">
                Identificamos <strong>{minorIndices.length}</strong> viajante(s) menor(es) de 18 anos na data da viagem. Informe o vínculo com o acompanhante para saber quais documentos serão exigidos.
              </p>
              <div className="space-y-4">
                {minorIndices.map(idx => {
                  const t = travelers[idx];
                  const age = ageAtTrip(t);
                  const evalR = minorEvaluations[idx];
                  const companion = minorCompanions[idx] || '';
                  return (
                    <div key={idx} className="bg-white p-4 rounded border border-purple-200" data-testid={`minor-block-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-gray-900">{t.fullName || `Viajante ${idx + 1}`}</p>
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{age} anos na viagem</span>
                      </div>
                      <div className="mb-3">
                        <Label className="text-xs">Vínculo do acompanhante desse menor</Label>
                        <Select value={companion} onValueChange={(v) => setCompanionType(idx, v)}>
                          <SelectTrigger data-testid={`minor-companion-${idx}`}><SelectValue placeholder="Selecione o vínculo" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both_parents">Ambos os pais</SelectItem>
                            <SelectItem value="father">Pai</SelectItem>
                            <SelectItem value="mother">Mãe</SelectItem>
                            <SelectItem value="legal_guardian">Responsável legal</SelectItem>
                            <SelectItem value="grandparent">Avô/Avó</SelectItem>
                            <SelectItem value="sibling">Irmão/Irmã</SelectItem>
                            <SelectItem value="uncle_aunt">Tio/Tia</SelectItem>
                            <SelectItem value="other_relative">Outro parente</SelectItem>
                            <SelectItem value="third_party_adult">Terceiro maior (não parente)</SelectItem>
                            <SelectItem value="unaccompanied">Desacompanhado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {evalR && (
                        <div className="text-sm bg-amber-50 border border-amber-200 p-3 rounded" data-testid={`minor-eval-${idx}`}>
                          <p className="font-semibold text-amber-900 mb-1">Situação: {evalR.situation}</p>
                          {evalR.required_documents && evalR.required_documents.length > 0 ? (
                            <>
                              <p className="text-amber-800 mb-1">Documentos exigidos:</p>
                              <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
                                {evalR.required_documents.map((d, i) => (<li key={i}>{d}</li>))}
                              </ul>
                            </>
                          ) : (
                            <p className="text-emerald-800 text-xs">Sem exigências documentais adicionais.</p>
                          )}
                          {evalR.notes && (<p className="text-xs text-gray-700 mt-2 italic">{evalR.notes}</p>)}
                          <p className="text-xs text-purple-800 mt-2">Após a compra, você poderá anexar os documentos em <strong>Área do Cliente → Minha Reserva</strong>.</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Botões */}
        <div className="flex gap-4 pt-8">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Voltar
          </Button>
          <Button
            onClick={handleContinue}
            className="flex-1 bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold"
          >
            Continuar para Embarque
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutStep2;
